/**
 * AppDrawer — the canonical right-side overlay chassis (ux-overhaul-hld.md
 * §8a "Canonical primary action + one create shell"). First consumer is the
 * notifications drawer (§7); Phase 3 create/edit forms reuse it as-is.
 *
 * Fixed inset-0 backdrop + a right-side panel that slides in from the edge.
 * Owns: focus trap while open, Escape-to-close (with an optional dirty-close
 * confirm), returning focus to the trigger on close, and locking body scroll
 * so only the drawer body scrolls. Header/body/footer are plain slots — the
 * caller supplies markup, this component only owns the chrome + behavior.
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AppDrawerWidth = 'sm' | 'md' | 'lg';

const WIDTH_CLASSES: Record<AppDrawerWidth, string> = {
  sm: 'w-full sm:w-[360px]',
  md: 'w-full sm:w-[560px]',
  lg: 'w-full sm:w-[720px]',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface AppDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  width?: AppDrawerWidth;
  footer?: ReactNode;
  children: ReactNode;
  /** When true, closing via Escape/backdrop/close-button asks for
   * confirmation first (an edited form guarding against accidental loss). */
  dirty?: boolean;
}

export function AppDrawer({ open, onClose, title, description, width = 'md', footer, children, dirty = false }: AppDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('Discard changes?')) return;
    onClose();
  }, [dirty, onClose]);

  // Focus trap + initial focus + return focus on close.
  useEffect(() => {
    if (!open) return undefined;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null,
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [open, requestClose]);

  // Lock body scroll while the drawer is open so only the panel scrolls.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100000]" data-testid="app-drawer">
      <div
        className="absolute inset-0 bg-black/30"
        data-testid="app-drawer-backdrop"
        aria-hidden="true"
        onClick={requestClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'fixed inset-y-0 right-0 flex h-full flex-col bg-white shadow-xl',
          'motion-safe:animate-app-drawer-in motion-reduce:animate-app-drawer-in-reduced',
          WIDTH_CLASSES[width],
        )}
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">{children}</div>

        {footer && <div className="flex-shrink-0 border-t border-gray-100 p-4">{footer}</div>}
      </div>
    </div>
  );
}
